import { API } from "@swivl-mono/api-helper";
import { LocationScope } from "@swivl-mono/types";
import { Button, Spinner } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import ErrorView from "../../../Components/ErrorView/ErrorView";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";

interface Props {
  dashboardId: string;
  title: string;
}

export function ReportsDashboard({ dashboardId, title }: Props) {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const page = usePropertiesGetLocationIds();
  const { locations } = LocationsModel.useState();

  // Compute locationIds based on the current view scope
  const locationIds = useMemo(() => {
    if (page.type === LocationScope.LOCATION && page.locationId) {
      // Single location view
      return String(page.locationId);
    }
    if (page.type === LocationScope.GROUP && page.groupId) {
      // Group view - include all locations in this group
      const groupLocationIds = locations?.filter((loc) => loc.groupId === page.groupId).map((loc) => loc.id) || [];
      return groupLocationIds.join(",");
    }
    // Org view - include all locations
    const allLocationIds = locations?.map((loc) => loc.id) || [];
    return allLocationIds.join(",");
  }, [page.type, page.groupId, page.locationId, locations]);

  useEffect(() => {
    if (!locationIds) {
      // Wait for locations to load
      return;
    }

    setIframeUrl(null);
    setError(null);
    setIsIframeLoading(true);

    API.Omni.getOmniIFrameURL(Member.orgId, dashboardId, locationIds)
      .then(setIframeUrl)
      .catch((err) => setError(err.message || "Failed to load dashboard"));
  }, [dashboardId, locationIds]);

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ErrorView />
      </div>
    );
  }

  if (!iframeUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      {isIframeLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </div>
      )}
      {Member.isSwivlStaff && (
        <div className="absolute top-4 right-4 z-100">
          <Button
            onClick={() =>
              API.Omni.getOmniEmbedToken(Member.orgId).then((token) =>
                navigator.clipboard.writeText(
                  `<iframe src="${import.meta.env.VITE_SERVER_URL}/api/omni/org/${Member.orgId}/dashboard/${dashboardId}/embed?token=${token}" width="100%" height="800" frameborder="0"></iframe>`
                )
              )
            }
          >
            Copy iframe
          </Button>
        </div>
      )}
      <iframe src={iframeUrl} title={title} className="h-full w-full border-0" onLoad={() => setIsIframeLoading(false)} />
    </div>
  );
}
